import InputField from "@/components/InputField";
import { BaseDropdownOption } from "@/components/dropdowns/BaseDropdown";
import SimpleDropdown from "@/components/dropdowns/SimpleDropdown/SimpleDropdown";
import Toolbar from "@/containers/Toolbar";
import { cn } from "@/lib/utils";
import {
  SBCCategory,
  SBCSetSortOption,
  SBC_SET_CATEGORIES,
} from "@/redux/sbcs/sbcSetsSlice";
import { BREAKPOINTS } from "@/settings";
import { useMemo } from "react";
import { useMediaQuery } from "usehooks-ts";

export const SBC_SORT_OPTIONS: BaseDropdownOption[] = [
  {
    label: "Most Upvoted",
    value: "most-upvoted",
  },
  {
    label: "Price - High to Low",
    value: "price-high-to-low",
  },
  {
    label: "Price - Low to High",
    value: "price-low-to-high",
  },
  {
    label: "In-progress",
    value: "in-progress",
  },
  {
    label: "Expiration",
    value: "expiration",
  },
  {
    label: "Newest",
    value: "newest",
  },
  {
    label: "Meta Rating",
    value: "meta-rating",
  },
];

interface SbcSetsPageToolbarProps {
  currentSortBy: SBCSetSortOption;
  handleSortByChanged: (option: SBCSetSortOption) => void;
  handleSearch: (searchTerm: string) => void;
  searchTerm: string;
  currentCategory: SBCCategory;
  handleCategoryChanged: (category: SBCCategory) => void;
}

interface CategoryButton {
  label: string;
  isActive: boolean;
  onClick: () => void;
}
const CategoryButton = ({ label, isActive, onClick }: CategoryButton) => (
  <button
    className={cn(
      "text-sm font-medium px-4 touch-device:px-2 py-1.5 flex justify-start items-center gap-x-1 select-none whitespace-nowrap relative text-left touch-device:rounded",
      isActive
        ? "text-white max-touch-device:bg-panel-middleground"
        : "text-dark-tertiary hover:text-dark-secondary hover:bg-panel-foreground",
    )}
    onClick={onClick}
  >
    {label}
  </button>
);

export const SbcSetsPageToolbar = ({
  currentSortBy,
  handleSortByChanged,
  handleSearch,
  searchTerm,
  currentCategory,
  handleCategoryChanged,
}: SbcSetsPageToolbarProps) => {
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);

  const sbcSetCategories = useMemo(
    () =>
      SBC_SET_CATEGORIES.map((category) => (
        <CategoryButton
          key={category.label}
          label={category.label}
          isActive={
            category.label?.toLowerCase() ===
            currentCategory.label?.toLowerCase()
          }
          onClick={() => handleCategoryChanged(category)}
        />
      )),
    [currentCategory, handleCategoryChanged],
  );

  return (
    <div className="flex flex-col w-full relative">
      <Toolbar color="bg-panel-background">
        <div className="flex flex-row justify-between w-full h-full relative items-center">
          <div className="w-54">
            <InputField
              type="search"
              placeholder="Search"
              autoFocus={isMobile}
              value={searchTerm}
              onChange={(value) => handleSearch(value)}
              dismissSearch={() => {
                handleSearch("");
              }}
            />
          </div>
          {!isTouchDevice && (
            <div className="flex flex-row gap-x-2 absolute left-1/2 -translate-x-1/2">
              {sbcSetCategories}
            </div>
          )}
          <div className="flex flex-row items-center text-sm gap-x-2 text-dark-tertiary whitespace-nowrap">
            <span className="hidden desktop:block">Sort by:</span>
            <SimpleDropdown
              variant="tertiary"
              options={SBC_SORT_OPTIONS}
              selectedOption={currentSortBy}
              onChange={(option) =>
                handleSortByChanged(option as SBCSetSortOption)
              }
            />
          </div>
        </div>
      </Toolbar>
      {isTouchDevice && (
        <>
          <div className="flex flex-row justify-start h-15 border-b border-panel-middleground overflow-x-auto absolute left-1/2 -translate-x-1/2 top-15 w-screen">
            {sbcSetCategories}
          </div>
          <div className="h-15" />
        </>
      )}
    </div>
  );
};
